import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import editIcon from "../../../../../../assets/png/edit.png";
import "./KeyCharactersPanel.css";

const parseCharacter = (str) => {
  const colonIndex = str.indexOf(":");
  if (colonIndex === -1) return { name: str.trim(), description: "" };
  return {
    name: str.substring(0, colonIndex).trim(),
    description: str.substring(colonIndex + 1).trim(),
  };
};

const formatCharacter = (name, description) =>
  description ? `${name}: ${description}` : name;

const KeyCharactersPanel = ({ keyCharacters = [], onSave, loading, onCollapse }) => {
  const { t } = useTranslation();
  const [characters, setCharacters] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  useEffect(() => {
    setCharacters(Array.isArray(keyCharacters) ? keyCharacters : []);
  }, [keyCharacters]);

  const handleAdd = () => {
    setEditingIndex(-1);
    setEditName("");
    setEditDescription("");
  };

  const handleEdit = (index) => {
    const { name, description } = parseCharacter(characters[index]);
    setEditingIndex(index);
    setEditName(name);
    setEditDescription(description);
  };

  const handleDelete = (index) => {
    const updated = characters.filter((_, i) => i !== index);
    setCharacters(updated);
    onSave(updated);
  };

  const handleSave = () => {
    if (!editName.trim()) return;
    const formatted = formatCharacter(editName.trim(), editDescription.trim());
    let updated;
    if (editingIndex === -1) {
      updated = [...characters, formatted];
    } else {
      updated = characters.map((c, i) => (i === editingIndex ? formatted : c));
    }
    setCharacters(updated);
    setEditingIndex(null);
    onSave(updated);
  };

  const handleCancel = () => {
    setEditingIndex(null);
  };

  return (
    <div className="key-characters-panel">
      <div className="key-characters-header">
        <div className="key-characters-header-left">
          <span className="key-characters-title">
            {t("key_characters")}
            <span className="key-characters-count">{characters.length}</span>
          </span>
        </div>
        {onCollapse && (
          <button
            className="key-characters-collapse-btn"
            onClick={onCollapse}
            title="Collapse sidebar"
          >
            <i className="fas fa-chevron-left" />
          </button>
        )}
      </div>

      <div className="key-characters-body">
          <p
            className="key-characters-help"
            style={{
              fontSize: "13px",
              color: "#6c757d",
              margin: "0 0 12px 0",
              lineHeight: 1.4,
            }}
          >
            {t("key_characters_help")}
          </p>
          {characters.length === 0 && editingIndex !== -1 && (
            <p className="key-characters-empty">{t("no_characters_yet")}</p>
          )}

          {characters.map((char, index) => {
            const { name, description } = parseCharacter(char);

            if (editingIndex === index) {
              return (
                <div key={index} className="character-edit-form">
                  <input
                    className="character-input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder={t("character_name")}
                    autoFocus
                  />
                  <textarea
                    className="character-input character-textarea"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder={t("character_description")}
                    rows={2}
                  />
                  <div className="character-edit-actions">
                    <button
                      className="aai-btn btn-pill-solid character-save-btn"
                      onClick={handleSave}
                      disabled={loading}
                    >
                      {t("save")}
                    </button>
                    <button
                      className="character-cancel-btn"
                      onClick={handleCancel}
                    >
                      {t("cancel")}
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <div key={index} className="character-item">
                <div className="character-info">
                  <span className="character-name">{name}</span>
                  {description && (
                    <span className="character-description">{description}</span>
                  )}
                </div>
                <div className="character-actions">
                  <button
                    className="character-action-btn"
                    onClick={() => handleEdit(index)}
                    title={t("edit")}
                    disabled={loading}
                  >
                    <img src={editIcon} alt="Edit" width={16} height={16} />
                  </button>
                  <button
                    className="character-action-btn character-delete-btn"
                    onClick={() => handleDelete(index)}
                    title={t("delete")}
                    disabled={loading}
                  >
                    <i className="fas fa-trash" />
                  </button>
                </div>
              </div>
            );
          })}

          {editingIndex === -1 && (
            <div className="character-edit-form">
              <input
                className="character-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={t("character_name")}
                autoFocus
              />
              <textarea
                className="character-input character-textarea"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder={t("character_description")}
                rows={2}
              />
              <div className="character-edit-actions">
                <button
                  className="aai-btn btn-pill-solid character-save-btn"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {t("save")}
                </button>
                <button
                  className="character-cancel-btn"
                  onClick={handleCancel}
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          )}

          {editingIndex === null && (
            <button
              className="add-character-btn"
              onClick={handleAdd}
              disabled={loading}
            >
              <i className="fas fa-plus" /> {t("add_character")}
            </button>
          )}
        </div>
    </div>
  );
};

export default KeyCharactersPanel;
